import { requestGet } from "../../utils/request.ts";
import { AbstractTask, TaskType } from "./task.ts";
import { getDirectoryHandler, getFileHandler, removeEntry } from "../../utils/opfs_common.ts";
import { OPFSFile } from "../../utils/opfs_utils.ts";
import { CacheKV } from "../../utils/cache_kv.ts";
import { $username } from "../user_info.ts";
import { decodePhoto, withLoadTimeout } from "../../utils/image_loading.ts";

export interface PhotoInfo {
    photo_id: number;
    photo_name: string;
    photo_time: number;
    photo_cursor?: string;
    analysis?: Record<string, any>;
}

interface PhotoImage {
    photo_id: number;
    photo_url: string;
}

interface GetPhotoResponse {
    total: number;
    photos: PhotoInfo[];
}

interface GetPhotoImageResponse {
    photos: PhotoImage[];
}

const PHOTO_CACHE_NAME = "photo_cache_v2";

export const getAllPhotos = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
) => {
    const photos: PhotoInfo[] = [];
    let total = 0;
    let cursor: string | undefined;
    const before = beforeTime ?? Date.now() / 1000;
    do {
        const resp = await requestGet<GetPhotoResponse>("/v2/photo", {
            camera_id: cameraId,
            before,
            after: afterTime ?? 0,
            limit: limit,
            reverse: 0,
            cursor,
        });
        total = resp.total;
        photos.push(...resp.photos);
        if (resp.photos.length === 0 || total <= resp.photos.length) {
            break;
        }
        const nextCursor = resp.photos.at(-1)?.photo_cursor;
        if (!nextCursor || nextCursor === cursor) {
            throw new Error("Photo pagination cursor missing or unchanged");
        }
        cursor = nextCursor;
    } while (true);
    return photos;
};

export const getPhotos = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
    reverse: boolean = false,
    cursor?: string,
    signal?: AbortSignal,
) => {
    const resp = await withLoadTimeout(attemptSignal => requestGet<GetPhotoResponse>("/v2/photo", {
        camera_id: cameraId,
        before: beforeTime ?? Date.now() / 1000,
        after: afterTime ?? 0,
        limit: limit,
        reverse: reverse ? 1 : 0,
        cursor,
    }, attemptSignal), signal);
    return resp.photos;
};

export const getPhotosWithTotal = async (
    cameraId: number,
    beforeTime: number | undefined = undefined,
    afterTime: number | undefined = undefined,
    limit: number = 500,
    reverse: boolean = false,
    cursor?: string,
    signal?: AbortSignal,
) => {
    const resp = await withLoadTimeout(attemptSignal => requestGet<GetPhotoResponse>("/v2/photo", {
        camera_id: cameraId,
        before: beforeTime ?? Date.now() / 1000,
        after: afterTime ?? 0,
        limit: limit,
        reverse: reverse ? 1 : 0,
        cursor,
    }, attemptSignal), signal);
    return resp;
};

export const clearVideoPhotoCache = async (photoId: number | undefined = undefined) => {
    if (photoId !== undefined) {
        const name = photoId.toString() + DownloadVideoPhotosTask.IMG_POSTFIX;
        const handler = await getFileHandler([...DownloadVideoPhotosTask.OPFS_PREFIX, name], false, false);
        await removeEntry(handler);
    } else {
        try {
            const handler = await getDirectoryHandler(DownloadVideoPhotosTask.OPFS_PREFIX, false);
            await removeEntry(handler);
        } catch (_) {
            // ignore
        }
    }
};

export const getPhotoImageURL = async (
    photoId: number,
    mode: "thumbnail" | "timeline" | "video" | "view" | "4k" | "download" = "thumbnail",
    signal?: AbortSignal,
): Promise<string> => {
    const resp1 = await withLoadTimeout(attemptSignal => requestGet<GetPhotoImageResponse>("/v2/photo_img", {
        photo_id: photoId.toString(),
        mode: mode ?? "thumbnail",
    }, attemptSignal), signal);
    const url = resp1.photos.find(photo => photo.photo_id === photoId)?.photo_url;
    if (!url) throw new Error("Photo is missing or no longer accessible");
    return url;
};

export const getPhotoImageData = async (
    photoId: number,
    mode: "thumbnail" | "timeline" | "video" | "view" | "4k" | "download" = "thumbnail",
    signal?: AbortSignal,
): Promise<ArrayBuffer> => {
    signal?.throwIfAborted();
    const cacheKey = `${$username.get()}|${mode}|${photoId.toString()}`;
    const kv = await CacheKV.open(PHOTO_CACHE_NAME);
    const cached = await kv.get(cacheKey);
    if (cached) {
        try {
            const decoded = await decodePhoto(cached, signal);
            URL.revokeObjectURL(decoded.url);
            return cached;
        } catch (error) {
            signal?.throwIfAborted();
            await kv.delete(cacheKey);
        }
    }

    for (let attempt = 0; attempt < 2; attempt++) {
        try {
            const data = await withLoadTimeout(async (attemptSignal) => {
                const response = await requestGet<GetPhotoImageResponse>("/v2/photo_img", {
                    photo_id: photoId.toString(),
                    mode: mode ?? "thumbnail",
                }, attemptSignal);
                const url = response.photos.find(photo => photo.photo_id === photoId)?.photo_url;
                if (!url) throw new Error("Photo is missing or no longer accessible");
                const imageResponse = await fetch(url, { mode: "cors", signal: attemptSignal });
                if (!imageResponse.ok) {
                    throw new Error(`Fetch image failed: ${imageResponse.status} ${imageResponse.statusText}`);
                }
                const buffer = await imageResponse.arrayBuffer();
                const decoded = await decodePhoto(buffer, attemptSignal);
                URL.revokeObjectURL(decoded.url);
                attemptSignal.throwIfAborted();
                return buffer;
            }, signal);
            signal?.throwIfAborted();
            await kv.set(cacheKey, data);
            return data;
        } catch (error) {
            signal?.throwIfAborted();
            if (attempt === 1) throw error;
            // Obtain a fresh signature as well as retrying the image request.
        }
    }
    throw new Error("Image load failed");
};

class VideoPhotoMemoryLimitError extends Error {}

export class DownloadVideoPhotosTask extends AbstractTask {
    static OPFS_PREFIX = ["CachedPhotos"];
    static IMG_POSTFIX = ".webp";
    static MEMORY_LIMIT_BYTES = 128 * 1024 * 1024;
    #photos: PhotoInfo[];
    #files: File[];
    #processNum = 0;
    #processed = 0;
    #memoryBytes = 0;
    constructor(photos: PhotoInfo[]) {
        super(TaskType.Download, photos.length);
        this.#photos = [...photos];
        this.#files = new Array(photos.length);
    }
    async #getImageFile(photoId: number, cacheDir: FileSystemDirectoryHandle | null, abortSignal: AbortSignal) {
        const name = photoId.toString() + DownloadVideoPhotosTask.IMG_POSTFIX;
        abortSignal.throwIfAborted();
        if (cacheDir) {
            try {
                const photoFile = await (await cacheDir.getFileHandle(name, { create: false })).getFile();
                // Older releases could cache an HTTP error body under an image filename.
                const bitmap = await createImageBitmap(photoFile);
                bitmap.close();
                abortSignal.throwIfAborted();
                return photoFile;
            } catch (_) {
                abortSignal.throwIfAborted();
            }
        }
        const data = await withLoadTimeout(async signal => {
            const resp1 = await requestGet<GetPhotoImageResponse>("/v2/photo_img", {
                photo_id: photoId.toString(),
                mode: "video",
            }, signal);
            const url = resp1.photos.find(photo => photo.photo_id === photoId)?.photo_url;
            if (!url) throw new Error("Photo is missing or no longer accessible");
            const resp2 = await fetch(url, {
                mode: "cors",
                signal,
            });
            if (!resp2.ok) {
                throw new Error(`Fetch image failed: ${resp2.status} ${resp2.statusText}`);
            }
            const data = await resp2.arrayBuffer();
            const bitmap = await createImageBitmap(new Blob([data]));
            bitmap.close();
            signal.throwIfAborted();
            return data;
        }, abortSignal);
        abortSignal.throwIfAborted();
        if (!cacheDir) {
            if (this.#memoryBytes + data.byteLength > DownloadVideoPhotosTask.MEMORY_LIMIT_BYTES) {
                throw new VideoPhotoMemoryLimitError(
                    "Browser file storage is unavailable and temporary video photos exceed the 128 MiB limit. " +
                    "Select fewer photos, or use HTTPS with a browser that supports file storage.",
                );
            }
            const file = new File([data], name, { type: "image/webp" });
            this.#memoryBytes += data.byteLength;
            return file;
        }

        const handler = await cacheDir.getFileHandle(name, { create: true });
        const opfsFile = new OPFSFile(handler);
        // write() transfers the buffer to a Worker, detaching it on this thread.
        const expectedSize = data.byteLength;
        try {
            try {
                await opfsFile.open();
                await opfsFile.truncate(0);
                const written = await opfsFile.write(data);
                if (written !== expectedSize) throw new Error("Incomplete image cache write");
            } finally {
                await opfsFile.close();
            }
            abortSignal.throwIfAborted();
            const photoFile = await handler.getFile();
            if (photoFile.size !== expectedSize) throw new Error("Incomplete image cache file");
            return photoFile;
        } catch (error) {
            await cacheDir.removeEntry(name).catch(() => {});
            throw error;
        }
    }
    async #downloadWorker(cacheDir: FileSystemDirectoryHandle | null, abortSignal: AbortSignal) {
        while (this.#processNum < this.#photos.length) {
            abortSignal.throwIfAborted();
            const idx = this.#processNum;
            this.#processNum += 1;
            if (this.#files[idx]) continue;
            
            // Try/catch for individual file download
            try {
                const photoFile = await this.#getImageFile(this.#photos[idx].photo_id, cacheDir, abortSignal);
                this.#files[idx] = photoFile;
                this.#processed += 1;
                this.$store.setKey("current", this.#processed);
            } catch (err) {
                console.warn(`Download failed for photo ${this.#photos[idx].photo_id}`, err);
                throw err;
            }
        }
    }
    override _action = async (abortSignal: AbortSignal) => {
        abortSignal.throwIfAborted();
        let cacheDir: FileSystemDirectoryHandle | null = null;
        try {
            cacheDir = await getDirectoryHandler(DownloadVideoPhotosTask.OPFS_PREFIX, true);
        } catch (error) {
            abortSignal.throwIfAborted();
            console.warn("Browser file storage unavailable; using bounded temporary video files.", error);
            this.$store.setKey("info", "File storage unavailable; using temporary memory (128 MiB photo limit).");
        }
        this.$store.setKey("current", 0);
        let retryCount = 0;
        
        // Mobile optimization: Limit concurrency to avoid memory issues
        const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
        const concurrency = isMobile ? 3 : 6;

        try {
            while (this.#processed < this.#photos.length) {
                abortSignal.throwIfAborted();
                // Keep successful files across retries, including memory-only files.
                this.#processNum = 0;
                const workers = [];
                for (let i = 0; i < concurrency; i++) {
                    workers.push(this.#downloadWorker(cacheDir, abortSignal));
                }
                const results = await Promise.allSettled(workers);
                abortSignal.throwIfAborted();
                const failures = results.filter((result): result is PromiseRejectedResult => result.status === "rejected");
                const memoryFailure = failures.find(result => result.reason instanceof VideoPhotoMemoryLimitError);
                if (memoryFailure) throw memoryFailure.reason;
                if (this.#processed === this.#photos.length) break;

                retryCount += 1;
                if (retryCount >= 3) {
                    const reason = failures[0]?.reason;
                    throw new Error(`Download Photo Failed: ${reason instanceof Error ? reason.message : String(reason ?? "Unknown error")}`);
                }
                console.log(`Download retry ${retryCount}... Processed: ${this.#processed}/${this.#photos.length}`);
                await new Promise((rs) => setTimeout(rs, 1000));
            }
            abortSignal.throwIfAborted();
        } catch (error) {
            this.#files = new Array(this.#photos.length);
            this.#memoryBytes = 0;
            this.#processNum = 0;
            this.#processed = 0;
            throw error;
        }
    };
    getDownloadedFiles() {
        return [...this.#files];
    }
}
